import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Lookup } from 'src/app/models/lookup.model';
import { EndpointService } from 'src/app/services/endpoint.service';

/**
 * Shape of a create request: the server assigns the primary key, so `id` is not part of the payload.
 * Sending `id: ''` for a UUID primary key is rejected by the API's JSON binder before the action runs —
 * an unset entity id is omitted, never an empty string. Typing the payload this way makes the compiler
 * reject `{ id: '', ... }` object literals at the call site.
 */
export type CreatePayload<T> = Omit<T, 'id'>;

export interface ICrudService<T> {
    getById(id: any): Observable<T>;

    getAll(filterBy: string): Observable<Lookup[]>;

    create(payload: CreatePayload<T>) : Observable<T>;

    update(payload: T) : Observable<Object>;

    patch(id: any, payload: Partial<T>) : Observable<Object>;

    delete(id: any) : Observable<Object>;

    createRange(payload: CreatePayload<T>[]) : Observable<Object>;
}

export abstract class CrudService<T> extends EndpointService implements ICrudService<T> {

    constructor(protected controllerName: string,
        http: HttpClient) {
        super(http);
    }

    getById(id: any): Observable<T> {
        return this.http.get<T>(this.getApiUrl(`${this.controllerName}/Get?id=${id}`));
    }

    getAll(filterBy: string = ''): Observable<Lookup[]> {
        return this.http.get<Lookup[]>(this.getApiUrl(`${this.controllerName}/GetAll?filterBy=${filterBy}`))
    }

    create(payload: CreatePayload<T>): Observable<T> {
        return this.http.post<T>(this.getApiUrl(`${this.controllerName}/Create`), payload);
    }

    update(payload: T): Observable<Object> {
        return this.http.post(this.getApiUrl(`${this.controllerName}/Update`), payload);
    }

    patch(id: any, payload: Partial<T>): Observable<Object> {
        return this.http.patch(this.getApiUrl(`${this.controllerName}/Patch/${id}`), payload);
    }

    delete(id: any): Observable<Object> {
        return this.http.delete(this.getApiUrl(`${this.controllerName}/Delete?id=${id}`));
    }

    createRange(payload: CreatePayload<T>[]): Observable<Object> {
        return this.http.post<CreatePayload<T>[]>(this.getApiUrl(`${this.controllerName}/CreateRange`), payload);
    }
}
